import { Injectable } from '@angular/core';
import { FlashCardModel } from 'src/app/api/models';
import {
  FlashCardsService,
  FlashCardStacksService,
} from 'src/app/api/services';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { ImportEntity, WizardImports } from '../../list/list.types';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ImportAiFlashcardComponent } from '../../common/dialogs/import-ai-flashcard/import-ai-flashcard.component';
import { AuthService } from 'src/app/auth/auth.service';
import { validateSchema } from 'src/app/utility/app.utils';
import { schemas } from 'src/app/payload-validators';
import { of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CommentListDialogComponent,
  CommentListDialogData,
} from '../../list/comment-list-dialog.component';
import { AmplitudeSource } from 'src/app/utility/amplitude-tracking/tracking.types';
import { Router } from '@angular/router';

/* TODO: 
  It used in books module which has old import options design.
  so this imports will be removed in future.
*/
export const getFlashCardImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<FlashCardModel>,
  courseContext = false,
  isAdmin = false
): ImportEntity<FlashCardModel>[] =>
  (
    [
      isAdmin
        ? {
            id: 'chat-gpt',
            title: translator.instant('StackAIQuestion.generateStackTitle'),
            description: translator.instant(
              'StackAIQuestion.generateStackDescription'
            ),
            highlight: true,
            action: {
              icon: 'code',
              click: () => {
                matDialog
                  .open<unknown, any>(ImportAiFlashcardComponent, {
                    data: {},
                    width: '600px',
                  })
                  .afterClosed()
                  .subscribe((r) => {
                    if (r) {
                      matDialog.closeAll();
                      panelService.fetchItems(panel);
                    }
                  });
              },
              text: translator.instant(
                'content.flashCard.addMenu.createSingleFlashcard'
              ),
            },
          }
        : null,
      {
        title: translator.instant(
          'content.flashCard.addMenu.generateFlashcardsWithChatGPT'
        ),
        description: translator.instant(
          'content.flashCard.description.generateFlashcardsWithChatGPT'
        ),
        action: {
          icon: 'code',
          click: () => {
            if (courseContext && panel) {
              panelService.importFromChatGPT(panel);
            } else {
              const callback = () => {
                const queryParams = {
                  type: PanelEntityType.FlashCard,
                };
                panelService.redirectToImportItems(
                  `/cms/csv-imports/flash-card`,
                  queryParams,
                  true
                );
              };
              panelService.importFromChatGPT(
                null,
                () => callback(),
                PanelEntityType.FlashCardStack
              );
            }
          },
          text: translator.instant(
            'content.flashCard.addMenu.generateFlashcards'
          ),
        },
      },
      courseContext
        ? {
            title: translator.instant(
              'content.flashCard.title.importExistingFlashcards'
            ),
            description: translator.instant(
              'content.flashCard.description.importExistingFlashcards'
            ),
            action: {
              icon: 'move_to_inbox',
              click: () => panelService.importItems(panel),
              text: translator.instant(
                'content.flashCard.addMenu.existingFlashcards'
              ),
            },
          }
        : null,
      {
        id: 'import-as-csv',
        title: translator.instant(
          'content.flashCard.title.importFlashcardFromTableExcelCSV'
        ),
        description: translator.instant(
          'content.flashCard.description.importFlashcardFromTableExcelCSV'
        ),
        action: {
          icon: 'description',
          click: () => {
            panelService.importItemAsCSV(panel, AmplitudeSource.STACK);
          },
          text: translator.instant(
            'content.flashCard.addMenu.flashcardsFromTableCSV'
          ),
        },
        extra: {
          name: translator.instant('content.question.title.downloadCSV'),
          type: 'link',
          icon: 'download',
          click: () => {
            window.open(
              'assets/import-csv-templates/Sample-flash-card-import.csv',
              '_blank'
            );
          },
        },
      },
      {
        id: 'import-json',
        title: translator.instant(
          'content.flashCard.title.importFlashcardFromJSON'
        ),
        description: translator.instant(
          'content.flashCard.description.importFlashcardFromJSON'
        ),
        action: {
          icon: 'code',
          click: () => panelService.importItemAsJSON(panel),
          text: translator.instant(
            'content.flashCard.addMenu.flashcardsFromJSON'
          ),
        },
      },
    ] as ImportEntity<FlashCardModel>[]
  ).filter((n) => n);

export const getWizardFlashCardImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<FlashCardModel>,
  router: Router,
  courseContext = false,
  isAdmin = false,
  allowAiForSingleCardGeneration = false
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createFlashCardWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        if (courseContext && panel) {
          panelService.importFromChatGPT(panel);
        } else {
          const callback = () => {
            const queryParams = {
              type: PanelEntityType.FlashCard,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/flash-card`,
              queryParams,
              true
            );
          };
          panelService.importFromChatGPT(
            null,
            () => callback(),
            PanelEntityType.FlashCardStack
          );
        }
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => {
        if (router) {
          router.navigate(['/cms/add/flash-card']);
        } else {
          panelService.editItem(panel);
        }
        matDialog.closeAll();
      },
    },
    importButtons: [
      {
        text: translator.instant(
          'content.wizardActions.title.createIndividualFlashcardsWithAI'
        ),
        description: translator.instant(
          'content.wizardActions.description.createIndividualQuestionsWithAI'
        ),
        icon: 'auto_awesome',
        hidden: !isAdmin || !allowAiForSingleCardGeneration,
        click: () => {
          matDialog
            .open<unknown, any>(ImportAiFlashcardComponent, {
              data: {},
              width: '600px',
            })
            .afterClosed()
            .subscribe((r) => {
              if (r) {
                matDialog.closeAll();
                panelService.fetchItems(panel);
              }
            });
        },
      },
      {
        text: translator.instant(
          'content.wizardActions.title.useExistingFlashcards'
        ),
        description: translator.instant(
          'content.wizardActions.description.useExistingFlashcards'
        ),
        icon: 'file_copy',
        hidden: !courseContext,
        click: () => panelService.importItems(panel),
      },
      {
        id: 'import-json',
        text: translator.instant('content.wizardActions.title.importFromJSON'),
        description: translator.instant(
          'content.wizardActions.description.importFromJSON'
        ),
        icon: 'save_alt',
        click: () => panelService.importItemAsJSON(panel),
      },
      {
        id: 'import-csv',
        text: translator.instant('content.wizardActions.title.importFromCSV'),
        description: translator.instant(
          'content.wizardActions.description.importFromCSV'
        ),
        icon: 'save_alt',
        click: () => {
          panelService.importItemAsCSV(panel, AmplitudeSource.STACK);
        },
      },
    ],
  };
};
@Injectable()
export class FlashCardPanel extends Panel<FlashCardModel> {
  constructor(
    flashCardStacks: FlashCardStacksService,
    flashCards: FlashCardsService,
    panelService: PanelService,
    translator: TranslateService,
    matDialog: MatDialog,
    auth: AuthService,
    matSnackBar: MatSnackBar
  ) {
    super();
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => panelService.editItem(this),
          text: translator.instant(
            'content.flashCard.addMenu.createNewFlashcard'
          ),
        },
      ],
      showChatGptAction:
        auth.currentUserHasGroup('Admin') ||
        auth.currentUserHasGroup('ContentManager'),
      wizardImports: getWizardFlashCardImports(
        translator,
        panelService,
        matDialog,
        this,
        null,
        true,
        auth.currentUserHasGroup('Admin') ||
          auth.currentUserHasGroup('ContentManager'),
        true
      ),
      imports: getFlashCardImports(
        translator,
        panelService,
        matDialog,
        this,
        true,
        auth.currentUserHasGroup('Admin') ||
          auth.currentUserHasGroup('ContentManager')
      ),
      changeItemPosition: (flashCardId, courseId, position) => {
        return flashCardStacks.repositionCardsInFlashCardStack({
          id: courseId,
          card_id: flashCardId,
          body: { position },
        });
      },
      createItemsFn: (items, parentId) => {
        const sentryInfo = {
          title: `Error while creating flashcard`,
          file: 'flash-card-panel.ts',
          api: 'copyCardsToFlashCardStack',
          preventLogs: true,
        };
        const { errorMessage, isInvalidSchema } = validateSchema(
          schemas.FlashCardModel.array(),
          items,
          matSnackBar,
          sentryInfo
        );
        if (isInvalidSchema) {
          return of({ error: errorMessage });
        }
        return flashCardStacks.copyCardsToFlashCardStack({
          id: parentId,
          body: items,
        });
      },
      deleteItem: (flashCardId) =>
        flashCards.deleteFlashCard({ id: flashCardId }),
      editDialogInput: (flashCard, flashCardStackId) => ({
        enableNavigation: true,
        flashCard,
        flashCardStackId,
      }),
      showTotalItems: true,
      fetchItem: (id) => flashCards.getFlashCardById({ id }),
      fetchItems: (flashCardStackId) =>
        flashCardStacks.getCardsInFlashCardStack({ id: flashCardStackId }),
      isFilteredListVisible: true,
      filterTopListBy: (flashCard) => flashCard.accepted,
      filterBottomListBy: (flashCard) => !flashCard.accepted,
      filteredListTitle: translator.instant('content.flashCard.suggestedCards'),
      itemActions: [
        {
          icon: 'question_answer',
          label: translator.instant('content.question.actionMenu.comments'),
          onClick: (flashCard) => {
            matDialog.open(CommentListDialogComponent, {
              data: {
                flashCardId: flashCard.id,
              },
              autoFocus: false,
            } as MatDialogConfig<CommentListDialogData>);
          },
        },
        {
          icon: 'code',
          label: translator.instant(
            'content.flashCard.actionMenu.downloadJSON'
          ),
          onClick: (item) => panelService.exportItemAsJSON(this, item.id),
        },
        {
          icon: 'link_off',
          label: translator.instant(
            'content.flashCard.actionMenu.removeFromStack'
          ),
          disabled: (card) => card['isImported'],
          tooltip: (card) =>
            card['isImported']
              ? translator.instant('content.quiz.notYourFlashcard')
              : null,
          onClick: (flashCard) => panelService.unlinkItems(this, flashCard.id),
        },
        {
          icon: 'delete',
          label: translator.instant('content.flashCard.actionMenu.delete'),
          disabled: (card) => card['isImported'],
          tooltip: (card) =>
            card['isImported']
              ? translator.instant('content.quiz.notYourFlashcard')
              : null,
          onClick: (flashCard) => panelService.deleteItem(this, flashCard.id),
        },
      ],
      headerIcon: 'crop_portrait',
      filteredItemAction: [
        {
          icon: 'delete',
          label: translator.instant('content.flashCard.actionMenu.delete'),
          disabled: (card) => card['isImported'],
          tooltip: (card) =>
            card['isImported']
              ? translator.instant('content.quiz.notYourFlashcard')
              : null,
          onClick: (flashCard) => panelService.deleteItem(this, flashCard.id),
        },
        {
          icon: 'add',
          label: translator.instant('common.tooltip.add'),
          onClick: (flashCard) => panelService.editItem(this, flashCard),
        },
      ],
      itemTextProperty: 'text',
      itemHeaderText: 'flashCard.table.text',
      parentKey: 'cards',
      parentType: PanelEntityType.FlashCardStack,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: translator.instant('content.flashCard.selectedItem.label'),
          onClick: (selectedItemIds) =>
            panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: translator.instant('content.flashCard.tooltip'),
        deleteItemError: translator.instant(
          'content.flashCard.deleteItem.error'
        ),
        deleteItemSuccess: translator.instant(
          'content.flashCard.deleteItem.success'
        ),
        deleteItemText: translator.instant('content.flashCard.deleteItem.text'),
        deleteItemTitle: translator.instant(
          'content.flashCard.deleteItem.title'
        ),
        header: translator.instant('common.label.quizFlashCard'),
        unlinkItemConfirmText: translator.instant(
          'content.flashCard.unlinkItem.text'
        ),
        unlinkItemConfirmTitle: translator.instant(
          'content.flashCard.unlinkItem.title'
        ),
        unlinkItemError: translator.instant(
          'content.flashCard.unlinkItem.error'
        ),
        unlinkItemSuccess: translator.instant(
          'content.flashCard.unlinkItem.success'
        ),
      },
      type: PanelEntityType.FlashCard,
      unlinkItems: (flashCardIds, flashCardStackId) => {
        return flashCardStacks.removeCardsFromFlashCardStack({
          id: flashCardStackId,
          card_ids: flashCardIds.join(','),
        });
      },
    });
  }
}
